import { getGetFrontAreaPubblicaRichiesteIdQueryOptions } from '@framework/api-client/api/inquiries-compile/area-pubblica-osservazioni-controller/area-pubblica-osservazioni-controller.ts';
import { getGetFrontRichiesteIdQueryOptions } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type RichiestaDTO } from '@framework/api-client/api/inquiries-compile/schemas/richiestaDTO.ts';
import { useGetQuery } from '@framework/api-client/utils/hooks/useGetQuery.ts';
import { useMemo } from 'react';
import { type UseQueryResult } from 'react-query';
import { useLocation } from 'react-router';
import { isPublicSubmission } from '../isPublicSubmission.ts';
import { SubmissionLogic } from '../SubmissionLogic.ts';

/**
 * Ritorna l'API corretta da chiamare in base al path fornito.
 * Nel caso di un path che contiene 'osservazioni' ritorna l'endpoint per le richieste pubbliche.
 * Altrimenti, ritorna l'endpoint per le richieste dell'utente.
 */
function getSubmissionEndpoint(path: string) {
  if (isPublicSubmission(path)) {
    // GET/area-pubblica/richieste/{id}
    return getGetFrontAreaPubblicaRichiesteIdQueryOptions;
  }

  // GET/richesta/{id}
  return getGetFrontRichiesteIdQueryOptions;
}

/**
 * Ritorna la richiesta in base all'id fornito, richiamando l'API
 * "privata" se si sta guardando una propria richiesta, o quella "pubblica"
 * se si cerca una richiesta pubblica. Il risultato dipende dall'URL in cui
 * ci si trova.
 */
export function useControlledAccessSubmission(
  submissionId: string | null | undefined,
) {
  const location = useLocation();

  const queryOptions = useMemo(
    () => getSubmissionEndpoint(location.pathname),
    [location.pathname],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  }: UseQueryResult<RichiestaDTO | undefined> = useGetQuery(queryOptions, [
    submissionId!,
    {
      query: {
        keepPreviousData: true,
        enabled: Boolean(submissionId),
      },
    },
  ]);

  // Trasformo il risultato con l'oggetto RichiestaDTO, uguale per entrambe le possibili chiamate
  const result = useMemo(() => {
    if (!data) return data;
    if ('richiesta' in data) {
      const result = data.richiesta as RichiestaDTO | undefined;
      if (!result) return result;
      // Se la richiesta è stata estratta correttamente, aggiorno
      // il campo "datiTitolare" rimuovendo tutti i soggetti non titolari
      return SubmissionLogic.filterSubmissionApplicantData(result);
    }
    return SubmissionLogic.filterSubmissionApplicantData(data);
  }, [data]);

  return {
    data: result ?? undefined,
    isLoading,
    isFetching,
    isError,
    error,
  };
}
